import './ensure-runtime-config'
import PrimeVue from 'primevue/config'
import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'
import { setupCalendar } from 'v-calendar'
import router from './router'
import App from './App.vue'
import store from './store/index'
import { useAuthStore } from './pinia/auth'
import i18n from './i18n'
import components from './components'
import { captureError, initPosthog, posthogEnabled } from './posthog'
import CookieConsentPlugin from './cookie-consent'
import { applyDbLocale, dbLocaleEn, dbLocaleSv } from '@digitalbuildings/zdb-design'
import '@digitalbuildings/zdb-design/style.css'
import './styles/base.scss'
import './styles/mapbox.scss'
import './styles/mixins.scss'
import './styles/navvis.scss'

initPosthog()

// A lazy chunk is gone when a deploy happens while the tab is open. Reloading
// fetches the new index and its chunks. The timestamp guard stops a reload loop
// if the chunk is really missing, in which case the error is left to surface.
// The error is captured first (a no-op when PostHog is off) so it is reported
// even when the page reloads, and the import() that follows may be caught
// somewhere and never reach the handlers below.
globalThis.addEventListener('vite:preloadError', (event) => {
  const key = 'preload_error_reloaded_at'
  let reloading = false
  try {
    const last = Number(sessionStorage.getItem(key) ?? 0)
    if (Date.now() - last >= 60_000) {
      sessionStorage.setItem(key, String(Date.now()))
      reloading = true
    }
  } catch {
    // No sessionStorage means no way to guard against a reload loop
  }
  captureError((event as Event & { payload: unknown }).payload, {
    source: 'vite_preload',
    reloading
  })
  if (!reloading) return
  event.preventDefault()
  globalThis.location.reload()
})

if (posthogEnabled) {
  globalThis.addEventListener('error', (event) => {
    captureError(event.error ?? event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    })
  })

  globalThis.addEventListener('unhandledrejection', (event) => {
    captureError(event.reason)
  })

  // Failed lazy route imports and throwing guards end up here. Without a
  // listener vue-router only logs them, e.g. on the initial navigation
  router.onError((error, to) => {
    captureError(error, { source: 'router', route_name: to.name, route_path: to.path })
  })
}

/**
 * Summary: Pinia is used for app state management
 */
const pinia = createPinia()

const app = createApp(App)
if (posthogEnabled) {
  app.config.errorHandler = (err, instance, info) => {
    captureError(err, {
      source: 'vue',
      vue_component: instance?.$options.name ?? instance?.$options.__name,
      vue_info: info
    })
  }
}
app.use(CookieConsentPlugin)
// PrimeVue's own texts (DbDatePicker's months and days, screen reader texts) follow the app's language.
const primeVueLocale = (locale: string) => (locale === 'sv' ? dbLocaleSv : dbLocaleEn)
app.use(PrimeVue, { unstyled: true, locale: primeVueLocale(i18n.global.locale.value) })
watch(i18n.global.locale, (locale) =>
  applyDbLocale(app.config.globalProperties.$primevue.config, primeVueLocale(locale))
)
app.use(pinia)
useAuthStore().initialize()
app.use(store)
app.use(router)
app.use(i18n)
app.use(setupCalendar, {})
Object.entries(components).forEach(([name, component]) => {
  app.component(name, component)
})
app.mount('#app')
